import type { ComponentType } from "react"
import { useState } from "react"
import { useNavigate, useSearch } from "@tanstack/react-router"
import {
	IconCheckOutline18 as Check,
	IconCursorRippleOutline18 as CursorRipple,
	IconEyeDashedOutline18 as EyeDashed,
	IconLayoutBottomOutline18 as LayoutBottom,
	IconLayoutHeaderOutline18 as LayoutHeader,
	IconMsgDottedOutline18 as MsgDotted,
	IconArrowRotateAnticlockwiseOutline18 as Reset,
	IconScaleOutline18 as Scale,
	IconSquareDashedOutline18 as SquareDashed,
} from "nucleo-ui-outline-18"

import { Button, SegmentedControl } from "@productlane/ui"

import type { DesignProps } from "./designs/design-props"
import type { DesignId, ShadowState } from "./shadow-mode-data"
import { isDevEnvironment } from "@/env"
import { NotFound } from "@/screens/not-found"
import { DesignHeaderPill } from "./designs/design-header-pill"
import { DesignHoverToolbar } from "./designs/design-hover-toolbar"
import { DesignInlineFooter } from "./designs/design-inline-footer"
import { DesignReviewBar } from "./designs/design-review-bar"
import { DesignVerdictRow } from "./designs/design-verdict-row"
import { isDesignId, isShadowState } from "./shadow-mode-data"

interface DesignOption {
	value: DesignId
	label: string
	Icon: ComponentType<React.SVGProps<SVGSVGElement>>
	note: string
	Component: ComponentType<DesignProps>
}

export const SHADOW_MODE_DESIGNS: readonly DesignOption[] = [
	{
		value: "1",
		label: "Inline footer",
		Icon: SquareDashed,
		note: "The agent bubble with a dashed edge, a Not sent badge, and Decline or Send in its footer. Decline opens the AI Assistant in the right panel.",
		Component: DesignInlineFooter,
	},
	{
		value: "2",
		label: "Hover toolbar",
		Icon: CursorRipple,
		note: "A ghost bubble with a dotted edge and an outlined agent avatar. The decision sits in a message toolbar. Decline answers inline, and the instruction change is already applied with undo.",
		Component: DesignHoverToolbar,
	},
	{
		value: "3",
		label: "Review bar",
		Icon: LayoutBottom,
		note: "A dashed rail marks the draft. The review bar docks above the reply box with Edit, Decline, and Send. Decline turns the reply box into an assistant drawer.",
		Component: DesignReviewBar,
	},
	{
		value: "4",
		label: "Header pill",
		Icon: LayoutHeader,
		note: "The message carries a Shadow reply band. The decision lives in a pill in the thread header. Decline opens the assistant as a popover under it.",
		Component: DesignHeaderPill,
	},
	{
		value: "5",
		label: "Verdict row",
		Icon: Scale,
		note: "A label sits on the dashed edge. A verdict row under the message asks for a reason before the assistant opens as an overlay.",
		Component: DesignVerdictRow,
	},
]

const STATE_OPTIONS = [
	{ value: "pending", label: "Waiting for review", Icon: MsgDotted },
	{ value: "declined", label: "Declined", Icon: EyeDashed },
	{ value: "sent", label: "Sent", Icon: Check },
] as const satisfies readonly {
	value: ShadowState
	label: string
	Icon: ComponentType<React.SVGProps<SVGSVGElement>>
}[]

export function AgentShadowModePage() {
	const navigate = useNavigate()
	const designParam =
		useSearch({ strict: false, select: (s) => s.design }) ?? null
	const stateParam =
		useSearch({ strict: false, select: (s) => s.state }) ?? null
	const design: DesignId = isDesignId(designParam) ? designParam : "1"
	const state: ShadowState = isShadowState(stateParam) ? stateParam : "pending"
	// Only a click on Decline plays the thinking step; links and the switcher land on the answer.
	const [instant, setInstant] = useState(true)
	const [resetKey, setResetKey] = useState(0)

	if (!isDevEnvironment) return <NotFound />

	const update = (next: { design?: DesignId; state?: ShadowState }) => {
		void navigate({
			to: ".",
			search: (prev) => ({
				...prev,
				...(next.design && { design: next.design }),
				...(next.state && { state: next.state }),
			}),
			replace: true,
		})
	}

	const option =
		SHADOW_MODE_DESIGNS.find((d) => d.value === design) ??
		SHADOW_MODE_DESIGNS[0]!
	const Design = option.Component

	return (
		<div className="flex h-full flex-col bg-gray-50 dark:bg-gray-950">
			<div className="flex flex-none flex-col gap-3 px-5 pb-3 pt-4">
				<div className="flex flex-wrap items-center gap-3">
					<div className="mr-2">
						<div className="text-sm font-semibold text-primary">
							Agent shadow mode
						</div>
						<div className="text-xs text-tertiary">
							Five drafts on thread #4821
						</div>
					</div>
					<SegmentedControl
						name="shadow-mode-design"
						value={design}
						options={SHADOW_MODE_DESIGNS.map(({ value, label, Icon }) => ({
							value,
							label: `${value}. ${label}`,
							Icon,
						}))}
						onValueChange={(value) => {
							setInstant(true)
							update({ design: value })
						}}
						showLabels
					/>
					<SegmentedControl
						name="shadow-mode-state"
						value={state}
						options={STATE_OPTIONS}
						onValueChange={(value) => {
							setInstant(true)
							update({ state: value })
						}}
						showLabels
					/>
					<Button
						variant="ghost"
						size="sm"
						Icon={Reset}
						onClick={() => {
							setInstant(true)
							setResetKey((key) => key + 1)
							update({ state: "pending" })
						}}
					>
						Reset
					</Button>
				</div>
				<p className="max-w-[60rem] text-xs text-secondary">{option.note}</p>
			</div>
			<div className="mx-3 mb-3 flex min-h-0 flex-1 overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700/80">
				<Design
					key={`${design}-${resetKey}`}
					state={state}
					instant={instant}
					onSend={() => update({ state: "sent" })}
					onDecline={() => {
						setInstant(false)
						update({ state: "declined" })
					}}
				/>
			</div>
		</div>
	)
}
